import styled from 'styled-components'
import { InvoicePaymentType } from '../../api/dto/invoice-delivery.dto'
import type { DrawStatusTone } from './InvoiceDeliveryTableWithStripePayment.styles'
import {
  financeControlSkin,
  FONT_STACK,
  sureco,
} from '../shared/finance.styles'

/* Same type and control skin as the billing profile pages. */
export const DrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: ${sureco.fg1};
  font-family: ${FONT_STACK};

  ${financeControlSkin}
`

export const DrawerNavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 10px;
  background: ${sureco.pageBg};
  border: 1px solid ${sureco.border};
  border-radius: 8px;
  margin-bottom: -8px;
`

export const DrawerNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid ${sureco.borderStrong};
  background: #ffffff;
  color: ${sureco.fg2};
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s;

  &:hover:not(:disabled) {
    background: #e6f6f7;
    border-color: ${sureco.teal};
    color: ${sureco.tealDark};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export const DrawerNavPosition = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${sureco.fg3};
  letter-spacing: 0.02em;
  white-space: nowrap;
`

export const DrawerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const DrawerSectionTitleText = styled.span`
  flex: 1;
`

export const DrawerSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${sureco.fg3};
  display: flex;
  align-items: center;
  min-height: 20px;
`

/*
 * Two-column field row used inside drawer sections. Label column is fixed
 * width so values align across rows in the same section, regardless of
 * label length.
 */
export const DrawerField = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  align-items: center;
  gap: 8px;
`

export const DrawerFieldLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${sureco.fg3};
`

export const DrawerFieldValue = styled.div`
  font-size: 13px;
  color: ${sureco.fg1};
  line-height: 1.5;
  min-width: 0;
`

/*
 * Hero card at the top of the drawer — file icon + filename + version on
 * one row, billing period and upload time on a quieter line below.
 */
export const DrawerFileCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: ${sureco.pageBg};
  border: 1px solid ${sureco.border};
  border-radius: 8px;
`

export const DrawerFileCardIcon = styled.div`
  font-size: 20px;
  color: #16a34a;
  flex-shrink: 0;
  line-height: 1;
  padding-top: 2px;
`

export const DrawerFileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`

export const DrawerFileCardName = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${sureco.fg1};
  line-height: 1.4;
  word-break: break-word;
`

export const DrawerFileCardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: ${sureco.fg3};
  line-height: 1.4;
`

export const DrawerEmailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

/*
 * Subtle email row — visually quieter than a heavy pill.
 */
export const DrawerEmailChip = styled.span<{ $variant: 'to' | 'cc' }>`
  display: inline-block;
  font-size: 13px;
  font-weight: 400;
  color: ${sureco.fg1};
  word-break: break-all;
`

export const DrawerNoEmailWarning = styled.div`
  font-size: 12px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 8px 12px;
  line-height: 1.5;
`

export const DrawerPaymentPill = styled.span<{ $type: InvoicePaymentType }>`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: ${({ $type }) =>
    $type === InvoicePaymentType.WIRE ? '#eff6ff' : '#f5f3ff'};
  color: ${({ $type }) =>
    $type === InvoicePaymentType.WIRE ? sureco.tealDark : '#6d28d9'};
`

export const DrawerHeaderTitle = styled.div`
  font-family: ${FONT_STACK};
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  & > span:first-child {
    font-size: 16px;
    font-weight: 600;
    color: ${sureco.fg1};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`

export const DrawerActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`

// Amber inline editor for unparseable numeric fields (e.g. Total Due "N/A").
/* Holds the drawer's editable fields, like the OE1 split card. */
export const DrawerEditCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
  border: 1px solid ${sureco.border};
  border-radius: 8px;
  background: ${sureco.pageBg};
`

export const DrawerInlineEdit = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const DrawerInlineEditLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${sureco.fg3};
`

export const DrawerInlineEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-picker {
    width: 100%;
  }
`

export const DrawerInlineEditHint = styled.div<{ $tone?: 'warning' }>`
  font-size: 12px;
  color: ${({ $tone }) =>
    $tone === 'warning' ? sureco.noticeText : sureco.fg3};
  line-height: 1.5;
`

export const DrawerInlineEditCellRef = styled.code`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  font-weight: 600;
  background: #fde68a;
  color: #78350f;
  padding: 1px 4px;
  border-radius: 3px;
`

export const DrawerWarningList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const DrawerWarningItem = styled.li`
  font-size: 12px;
  color: ${sureco.fg3};
  line-height: 1.5;
`

export const DrawBySubCompanyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const DrawBySubCompanyCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  background: #ffffff;
  border: 1px solid ${sureco.border};
  border-radius: 10px;
`

export const DrawBySubCompanyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export const DrawBySubCompanyErrorMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #dc2626;
  padding-top: 10px;
  border-top: 1px solid #fee2e2;
`

export const DrawBySubCompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const DrawBySubCompanyName = styled.div<{ $isWarning?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $isWarning }) => ($isWarning ? '#b45309' : sureco.fg1)};
`

export const DrawBySubCompanyBankLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  & > ${DrawBySubCompanyName} {
    min-width: 0;
  }
`

export const DrawBySubCompanyId = styled.div`
  font-size: 12px;
  color: ${sureco.fg3};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`

export const DrawBySubCompanyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`

export const DrawBySubCompanyStatus = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`

export const DrawBySubCompanyAmount = styled.div`
  font-size: 12px;
  color: ${sureco.placeholder};
`

export const DrawBySubCompanyEventSection = styled.div`
  border-top: 1px solid ${sureco.offWhite};
  padding-top: 8px;
`

export const DrawBySubCompanyEventToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${sureco.teal};
`

export const DrawBySubCompanyEventToggleIcon = styled.span`
  font-size: 10px;
  color: ${sureco.placeholder};
`

export const DrawBySubCompanyEventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`

export const DrawBySubCompanyEventRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  & + & {
    border-top: 1px solid ${sureco.offWhite};
    padding-top: 10px;
  }
`

export const DrawBySubCompanyEventTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

export const DrawBySubCompanyEventLabel = styled.span<{
  $tone: DrawStatusTone
}>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $tone }) =>
    ({
      neutral: sureco.fg2,
      progress: '#ca8a04',
      success: '#15803d',
      danger: '#dc2626',
    })[$tone]};
`

export const DrawBySubCompanyEventTime = styled.span`
  font-size: 12px;
  color: ${sureco.placeholder};
  white-space: nowrap;
  flex-shrink: 0;
`

export const DrawBySubCompanyEventBy = styled.span`
  font-size: 12px;
  color: ${sureco.fg3};
`

export const DrawBySubCompanyEventError = styled.div`
  font-size: 12px;
  color: #dc2626;
`
